import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Link } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
// import { formatDwellingUnit } from '../../utils/brandingUtils';
import { activityAPI, userAPI } from '../../services/api';
import '../../styles/Dashboard.css';
import '../../styles/ThemeOverrides.css';
import PageTitle from '../shared/PageTitle';
import { scrollToSectionTop } from '../../utils/scrollUtils';

// Utility function to determine if a color is dark (needs light text)
const isColorDark = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    // Calculate relative luminance using the formula from WCAG 2.0
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // If luminance is less than 0.5, it's a dark color
    return luminance < 0.5;
  }
  return false;
};

// Utility function to get contrast text color
// const getContrastTextColor = (backgroundColor) => {
//   return isColorDark(backgroundColor) ? '#ffffff' : '#000000';
// };

// Function to force theme styles
const forceThemeStyles = (blockTitleBgColor, blockTitleColor) => {
  // Determine the proper text color for contrast
  const contrastTextColor = isColorDark(blockTitleBgColor) ? '#ffffff' : blockTitleColor;
  
  // Create or get the existing style element
  let styleEl = document.getElementById('force-activity-styles');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'force-activity-styles';
    document.head.appendChild(styleEl);
  }
  
  // Apply styles with ultra-high specificity
  styleEl.textContent = `
    /* Force theme colors for activity log headers using block title colors with proper contrast */
    .card-header.theme-styled-header,
    .activity-container .card .card-header,
    .activity-container .card-header,
    div[class*="card-header"],
    .card-header[class*="bg-"] {
      background-color: ${blockTitleBgColor} !important;
      color: ${contrastTextColor} !important;
      border-color: ${blockTitleBgColor} !important;
    }
    
    /* Force theme colors for header text with proper contrast */
    .card-header.theme-styled-header h5,
    .card-header.theme-styled-header *,
    .activity-container .card .card-header h5,
    .activity-container .card-header h5,
    div[class*="card-header"] h5 {
      color: ${contrastTextColor} !important;
    }
    
    /* Badges on dark headers should remain visible */
    .card-header .badge,
    .theme-styled-header .badge,
    .activity-container .card-header .badge {
      background-color: rgba(255, 255, 255, 0.2) !important;
      color: ${contrastTextColor} !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
    }
    
    /* Force theme colors for pagination using theme primary color */
    .activity-container .pagination .page-link,
    .activity-container .page-link {
      color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
    
    .activity-container .pagination .page-link:hover,
    .activity-container .page-link:hover {
      color: white !important;
      background-color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
    
    .activity-container .pagination .page-item.active .page-link,
    .activity-container .page-item.active .page-link {
      color: white !important;
      background-color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
    
    .activity-container .pagination .page-item.disabled .page-link,
    .activity-container .page-item.disabled .page-link {
      color: #6c757d !important;
      background-color: #ffffff !important;
      border-color: #dee2e6 !important;
    }
    
    /* Additional high-specificity pagination selectors */
    .card-footer .pagination .page-link,
    .card-footer .page-link {
      color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
    
    .card-footer .pagination .page-link:hover,
    .card-footer .page-link:hover {
      color: white !important;
      background-color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
    
    .card-footer .pagination .page-item.active .page-link,
    .card-footer .page-item.active .page-link {
      color: white !important;
      background-color: var(--theme-primary-color, #0d6efd) !important;
      border-color: var(--theme-primary-color, #0d6efd) !important;
    }
  `;
};

const Activity = () => {
  const { settings } = useSettings();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const cardHeaderRef = useRef(null);
  const activityListHeaderRef = useRef(null);
  const [users, setUsers] = useState([]);
  const loadingTimeoutRef = useRef(null);
  
  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterUser, setFilterUser] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Enhance activities with user data - handle case with no users more gracefully
  const enrichActivitiesWithUserData = useCallback(async (activities) => {
    // If users list is empty or very small, fetch the latest user data
    if (users.length < 3) {
      try {
        console.log('User list seems incomplete, fetching latest user data...');
        const usersData = await userAPI.getAllUsers();
        if (usersData && usersData.data && usersData.data.length > 0) {
          console.log(`Fetched ${usersData.data.length} users`);
          setUsers(usersData.data);
          
          // Use this fresh data for mapping
          return activities.map(activity => {
            const user = usersData.data.find(u => u.id === activity.user_id);
            return {
              ...activity,
              userName: user ? user.name : (activity.user_id ? `User #${activity.user_id}` : 'System'),
              userRole: user ? user.role : (activity.user_id ? 'member' : 'system')
            };
          });
        }
      } catch (fetchError) {
        console.error('Error fetching fresh user data:', fetchError);
      }
    }
    
    // Map user data to activities, with safeguards
    try {
      return activities.map(activity => {
        const user = users.find(u => u.id === activity.user_id);
        // Default to better role guesses based on activity type
        let defaultRole = 'member';
        
        if (!activity.user_id) {
          defaultRole = 'system';
        } else if (activity.type === 'security') {
          defaultRole = 'guard';
        } else if (activity.type === 'settings' || activity.type === 'user') {
          defaultRole = 'admin';
        } else if (activity.type === 'complaint') {
          defaultRole = 'owner';
        }
        
        return {
          ...activity,
          userName: user ? user.name : (activity.user_id ? `User #${activity.user_id}` : 'System'),
          userRole: user ? user.role : defaultRole
        };
      });
    } catch (error) {
      console.error('Error enriching activities with user data:', error);
      // Return the original activities if enrichment fails with improved default roles
      return activities.map(activity => {
        // Make an educated guess about the role based on activity type
        let guessedRole = 'member';
        
        if (!activity.user_id) {
          guessedRole = 'system';
        } else if (activity.type === 'security') {
          guessedRole = 'guard';
        } else if (activity.type === 'settings' || activity.type === 'user') {
          guessedRole = 'admin';
        } else if (activity.type === 'complaint') {
          guessedRole = 'owner';
        }
        
        return {
          ...activity,
          userName: activity.user_id ? `User #${activity.user_id}` : 'System',
          userRole: guessedRole
        };
      });
    }
  }, [users]);
  
  // Set a loading timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      // If still loading after 15 seconds, force display mock data
      loadingTimeoutRef.current = setTimeout(() => {
        console.error('Loading timeout reached, displaying mock data');
        setError('Loading timed out. Displaying sample data instead.');
        setLoading(false);
        
        const mockData = generateMockActivities();
        setActivities(mockData);
        setTotalCount(mockData.length);
      }, 15000);
    } else {
      // Clear timeout if loading completes
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [loading]);
  
  // Function to refresh activities
  const refreshActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare filters for API
      const filters = {};
      if (filterType !== 'all') filters.type = filterType;
      if (filterDate) filters.date = filterDate;
      if (searchTerm) filters.search = searchTerm;
      if (filterUser !== 'all') filters.user_id = filterUser;
      
      // Fetch data from API
      const data = await activityAPI.getAll(filters);
      
      if (data && data.success) {
        const activitiesWithUserData = await enrichActivitiesWithUserData(data.activities || []);
        setActivities(activitiesWithUserData);
        setTotalCount(data.totalCount || data.activities?.length || 0);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (err) {
      console.error('Error refreshing activity data:', err);
      setError('Failed to refresh activity data');
      
      // Provide mock data as fallback
      const mockData = generateMockActivities();
      setActivities(mockData);
    } finally {
      setLoading(false);
    }
  };
  
  // Load activities data
  useEffect(() => {
    const fetchActivityData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('Fetching activity data with filters:', { 
          type: filterType, 
          date: filterDate, 
          search: searchTerm,
          user_id: filterUser
        });
        
        // Prepare filters for API
        const filters = {};
        if (filterType !== 'all') filters.type = filterType;
        if (filterDate) filters.date = filterDate;
        if (searchTerm) filters.search = searchTerm;
        if (filterUser !== 'all') filters.user_id = filterUser;
        
        // Add a timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000)
        );
        
        // Load users if needed - with timeout protection
        if (users.length === 0) {
          try {
            const usersPromise = userAPI.getAllUsers();
            const usersData = await Promise.race([usersPromise, timeoutPromise]);
            if (usersData && usersData.data) {
              setUsers(usersData.data);
            }
          } catch (error) {
            console.error('Error fetching users data:', error);
            // Create mock users if can't load real ones
            setUsers([
              { id: 12, name: 'Admin User', role: 'admin' },
              { id: 14, name: 'Security Guard', role: 'guard' },
              { id: 17, name: 'Property Owner', role: 'owner' }
            ]);
          }
        }
        
        // Fetch data from API - with timeout protection
        const dataPromise = activityAPI.getAll(filters);
        const data = await Promise.race([dataPromise, timeoutPromise]);
        console.log('Activity data received:', data);
        
        if (data && data.success) {
          const activitiesWithUserData = await enrichActivitiesWithUserData(data.activities || []);
          setActivities(activitiesWithUserData);
          setTotalCount(data.totalCount || data.activities?.length || 0);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setError(`Failed to load activity data: ${err.message}. Using sample data instead.`);
        
        // Provide mock data as fallback if API fails
        console.log('Using mock data as fallback');
        const mockData = generateMockActivities();
        setActivities(mockData);
        setTotalCount(mockData.length);
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivityData();
  }, [filterType, filterDate, searchTerm, filterUser, enrichActivitiesWithUserData, users.length]);
  
  // Apply theme colors to the card header
  useEffect(() => {
    const applyDirectStyling = (headerRef) => {
      if (headerRef.current) {
        const blockTitleBgColor = settings.block_title_bg_color || '#f8f9fa';
        const blockTitleColor = settings.block_title_color || '#8B4513';
        
        // Determine the proper text color for contrast
        const contrastTextColor = isColorDark(blockTitleBgColor) ? '#ffffff' : blockTitleColor;
        
        // Also set direct style attributes as a backup
        headerRef.current.style.backgroundColor = blockTitleBgColor;
        headerRef.current.style.color = contrastTextColor;
        
        // And set the h5 text color
        const headings = headerRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headings.forEach(heading => {
          heading.style.color = contrastTextColor;
        });
      }
    };

    // Apply direct styling to both headers
    applyDirectStyling(cardHeaderRef);
    applyDirectStyling(activityListHeaderRef);

    if (cardHeaderRef.current || activityListHeaderRef.current) {
      // Use block title colors for card headers (consistent with rest of app)
      const blockTitleBgColor = settings.block_title_bg_color || '#f8f9fa';
      const blockTitleColor = settings.block_title_color || '#8B4513';
      const primaryColor = settings.theme_primary_color || '#0d6efd';
      
      // Determine the proper text color for contrast
      const contrastTextColor = isColorDark(blockTitleBgColor) ? '#ffffff' : blockTitleColor;
      
      // Set CSS custom properties for theme colors - COMMENTED OUT TO PREVENT INLINE STYLES
      // const root = document.documentElement;
      // root.style.setProperty('--block-title-bg-color', blockTitleBgColor);
      // root.style.setProperty('--block-title-color', contrastTextColor);
      // root.style.setProperty('--theme-primary-color', primaryColor);
      // root.style.setProperty('--activity-theme-bg', blockTitleBgColor);
      // root.style.setProperty('--activity-theme-color', contrastTextColor);
      
      // Create a dedicated style tag with high specificity rules
      let styleEl = document.getElementById('activity-header-override');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'activity-header-override';
        document.head.appendChild(styleEl);
      }
      
      // Set the CSS with high specificity selectors using block title colors with proper contrast
      styleEl.textContent = `
        .theme-styled-header,
        .activity-container .theme-styled-header,
        .card-header.theme-styled-header,
        div.theme-styled-header {
          background-color: ${blockTitleBgColor} !important;
          color: ${contrastTextColor} !important;
        }
        
        .theme-styled-header h5,
        .activity-container .theme-styled-header h5,
        .card-header.theme-styled-header h5,
        div.theme-styled-header h5 {
          color: ${contrastTextColor} !important;
        }
        
        .activity-container .card-header, 
        .activity-container .card .card-header,
        #root .activity-container .card .card-header,
        .card-header[style],
        div.card-header {
          background-color: ${blockTitleBgColor} !important;
          color: ${contrastTextColor} !important;
        }
        
        .activity-container .card-header h5,
        .activity-container .card .card-header h5,
        #root .activity-container .card .card-header h5,
        .card-header h5 {
          color: ${contrastTextColor} !important;
        }
        
        /* Pagination theme colors with high specificity using primary color for interactivity */
        #root .activity-container .pagination .page-link,
        #root .activity-container .page-link,
        .activity-container .card-footer .pagination .page-link,
        .activity-container .card-footer .page-link {
          color: ${primaryColor} !important;
          border-color: rgba(0,0,0,0.125) !important;
        }
        
        #root .activity-container .pagination .page-link:hover,
        #root .activity-container .page-link:hover,
        .activity-container .card-footer .pagination .page-link:hover,
        .activity-container .card-footer .page-link:hover {
          color: white !important;
          background-color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }
        
        #root .activity-container .pagination .page-item.active .page-link,
        #root .activity-container .page-item.active .page-link,
        .activity-container .card-footer .pagination .page-item.active .page-link,
        .activity-container .card-footer .page-item.active .page-link {
          color: white !important;
          background-color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }
      `;
    }
    
    return () => {
      // Clean up the style element on unmount
      const styleEl = document.getElementById('activity-header-override');
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [settings, cardHeaderRef, activityListHeaderRef]);
  
  // Call force theme styles when settings change
  useEffect(() => {
    const blockTitleBgColor = settings.block_title_bg_color || '#f8f9fa';
    const blockTitleColor = settings.block_title_color || '#8B4513';
    
    forceThemeStyles(blockTitleBgColor, blockTitleColor);
  }, [settings]);
  
  // Handle pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  
  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('activity-table-section');
    }, 50);
  };
  
  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('activity-table-section');
      }, 50);
    }
  };
  
  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('activity-table-section');
      }, 50);
    }
  };
  
  // Generate mock activities for fallback if API fails
  const generateMockActivities = () => {
    console.log('Generating mock activities for fallback');
    
    // Current timestamp for generating relative times
    const now = Date.now();
    
    // Create fixed sample data instead of random data
    const mockActivities = [
      {
        id: 1,
        type: 'visitor',
        message: 'New visitor arrived at Unit A-101',
        timestamp: new Date(now - 1000 * 60 * 5).toISOString(),
        user_id: 14,  // Guard
        userName: 'Security Guard',
        userRole: 'guard'
      },
      {
        id: 2,
        type: 'payment',
        message: 'Maintenance fee payment of ₹2,000 received from Unit A-102',
        timestamp: new Date(now - 1000 * 60 * 30).toISOString(),
        user_id: 12,  // Admin
        userName: 'Admin User',
        userRole: 'admin'
      },
      {
        id: 3,
        type: 'complaint',
        message: 'New complaint filed for water leakage issue',
        timestamp: new Date(now - 1000 * 60 * 60).toISOString(),
        user_id: 17,  // Owner
        userName: 'Property Owner',
        userRole: 'owner'
      },
      {
        id: 4,
        type: 'user',
        message: 'User profile updated for Hitesh Owner',
        timestamp: new Date(now - 1000 * 60 * 120).toISOString(),
        user_id: 12,  // Admin
        userName: 'Admin User',
        userRole: 'admin'
      },
      {
        id: 5,
        type: 'settings',
        message: 'Society maintenance fee updated to ₹2,500',
        timestamp: new Date(now - 1000 * 60 * 180).toISOString(),
        user_id: 12,  // Admin
        userName: 'Admin User',
        userRole: 'admin'
      },
      {
        id: 6,
        type: 'system',
        message: 'Daily backup completed successfully',
        timestamp: new Date(now - 1000 * 60 * 240).toISOString(),
        user_id: null,
        userName: 'System',
        userRole: 'system'
      },
      {
        id: 7,
        type: 'security',
        message: 'Security check completed for all units',
        timestamp: new Date(now - 1000 * 60 * 300).toISOString(),
        user_id: 14,  // Guard
        userName: 'Security Guard', 
        userRole: 'guard'
      },
      {
        id: 8,
        type: 'complaint',
        message: 'Complaint #12 marked as resolved',
        timestamp: new Date(now - 1000 * 60 * 360).toISOString(),
        user_id: 12,  // Admin
        userName: 'Admin User',
        userRole: 'admin'
      }
    ];
    
    return mockActivities;
  };
  
  // Get appropriate icon for each activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'visitor': return 'fas fa-user-friends';
      case 'complaint': return 'fas fa-exclamation-circle';
      case 'payment': return 'fas fa-money-bill-wave';
      case 'user': return 'fas fa-user';
      case 'settings': return 'fas fa-cog';
      case 'system': return 'fas fa-server';
      case 'security': return 'fas fa-shield-alt';
      case 'maintenance': return 'fas fa-tools';
      case 'document': return 'fas fa-file-alt';
      case 'announcement': return 'fas fa-bullhorn';
      default: return 'fas fa-info-circle';
    }
  };
  
  // Format date for display
  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';
 
    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);
 
      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
 
      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }
  
  // Get relative time (e.g., "2 hours ago")
  const getRelativeTime = (timestamp) => {
    if (!timestamp || typeof timestamp !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = timestamp.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create UTC Date
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Convert both to IST by adding 5.5 hours (19800000 ms)
      const istOffsetMs = 5.5 * 60 * 60 * 1000;
      const istActivityTime = new Date(utcDate.getTime() + istOffsetMs);
      const istNow = new Date(Date.now() + istOffsetMs);  // ✅ this was missing!

      // Calculate time difference
      const diffMs = istNow - istActivityTime;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) return 'just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

      return 'over a week ago';
    } catch (err) {
      console.error('Invalid timestamp:', timestamp, err);
      return 'Invalid Date';
    }
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilterType('all');
    setFilterDate('');
    setFilterUser('all');
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  // Get badge color for user role
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-danger';
      case 'guard': return 'bg-warning text-dark';
      case 'owner': return 'bg-info';
      case 'system': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };
  
  // Get badge color for activity type
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'visitor': return 'bg-info';
      case 'complaint': return 'bg-danger';
      case 'payment': return 'bg-success';
      case 'user': return 'bg-primary';
      case 'settings': return 'bg-warning text-dark';
      case 'system': return 'bg-secondary';
      case 'security': return 'bg-dark';
      case 'maintenance': return 'bg-info text-dark';
      case 'document': return 'bg-light text-dark';
      case 'announcement': return 'bg-success';
      default: return 'bg-secondary';
    }
  };
  
  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle icon="fas fa-history" title="Activity Log" subtitle="Track all system activities" />
      
      {/* Filter Controls */}
      <div className="card shadow-sm mb-4">
        <div className="card-header theme-styled-header" ref={cardHeaderRef}>
          <h5 className="mb-0"><i className="fas fa-filter me-2"></i> Filter Activities</h5>
        </div>
        
        <div className="card-body">
          <div className="row g-3">
            <div className="col-lg-3 col-md-12">
              <label htmlFor="filterType" className="form-label">Activity Type</label>
              <select
                id="filterType"
                className="form-select"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <option value="all">All Types</option>
                <option value="unit">Unit</option>
                <option value="visitor">Visitor</option>
                <option value="complaint">Complaint</option>
                <option value="payment">Payment</option>
                <option value="user">User</option>
                <option value="settings">Settings</option>
                <option value="system">System</option>
                <option value="security">Security</option>
                <option value="maintenance">Maintenance</option>
                <option value="document">Document</option>
                <option value="announcement">Announcement</option>
              </select>
            </div>
            
            <div className="col-lg-3 col-md-12">
              <label htmlFor="filterDate" className="form-label">Date</label>
              <input
                type="date"
                id="filterDate"
                className="form-control"
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            
            <div className="col-lg-3 col-md-12">
              <label htmlFor="filterUser" className="form-label">User</label>
              <select
                id="filterUser"
                className="form-select"
                value={filterUser}
                onChange={(e) => {
                  setFilterUser(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Users</option>
                <option value="null">System</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="col-lg-3 col-md-12">
              <label htmlFor="searchTerm" className="form-label">Search</label>
              <div className="input-group">
                <input
                  type="text"
                  id="searchTerm"
                  className="form-control"
                  placeholder="Search in messages..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <button
                  className="btn btn-outline-secondary searchCloseFilter"
                  type="button"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="d-flex gap-2 flex-wrap justify-content-end mt-3">
            <button
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              <i className="fas fa-undo me-1"></i> Reset Filters
            </button>
            
            <button
              className="btn btn-primary"
              onClick={refreshActivities}
              disabled={loading}
            >
              <i className={`fas fa-${loading ? 'spinner fa-spin' : 'sync'} me-1`}></i>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}
      
      {/* Activities List */}
      <div className="card shadow-sm" id="activity-table-section">
        <div className="card-header d-flex justify-content-between align-items-center theme-styled-header" ref={activityListHeaderRef}>
          <h5 className="mb-0"><i className="fas fa-list me-2"></i> Activity List</h5>
          <span className="badge bg-primary">{totalCount} Activities</span>
        </div>
        
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading activities...</p>
            </div>
          ) : currentItems.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>Type</th>
                    <th style={{ width: '42%' }}>Message</th>
                    <th style={{ width: '20%' }}>User</th>
                    <th style={{ width: '15%' }}>Date & Time</th>
                    <th style={{ width: '15%' }}>Time Ago</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((activity) => (
                    <tr key={activity.id}>
                      <td>
                        <span className={`badge ${getTypeBadgeColor(activity.type)}`} style={{ width: '100%' }}>
                          <i className={`${getActivityIcon(activity.type)} me-1`}></i>
                          {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                        </span>
                      </td>
                      <td className="text-wrap break-word">{activity.message}</td>
                      <td>
                        {activity.user_id ? (
                          <div>
                            <span className="d-block">{activity.userName || `User #${activity.user_id}`}</span>
                            <span className={`badge ${getRoleBadgeColor(activity.userRole)} mt-1`}>
                              {activity.userRole || 'unknown'}
                            </span>
                          </div>
                        ) : (
                          <div>
                            <span className="d-block">System</span>
                            <span className="badge bg-secondary mt-1">automated</span>
                          </div>
                        )}
                      </td>
                      <td>{formatDate(activity.timestamp)}</td>
                      <td>{getRelativeTime(activity.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center p-5">
              <i className="fas fa-info-circle fa-3x text-muted mb-3"></i>
              <p className="mb-0">No activities found matching your filters.</p>
              <button className="btn btn-sm btn-outline-primary mt-3" onClick={resetFilters}>
                <i className="fas fa-undo me-1"></i> Reset Filters
              </button>
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {currentItems.length > 0 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <div>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, activities.length)} of {activities.length} activities
            </div>
            
            <nav aria-label="Activity pagination">
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                
                {/* Simplified pagination - show only certain page numbers */}
                {Array.from({ length: totalPages }).map((_, index) => {
                  const pageNumber = index + 1;
                  
                  // Show first page, last page, current page, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(pageNumber)}>
                          {pageNumber}
                        </button>
                      </li>
                    );
                  }
                  
                  // Show ellipsis
                  if (
                    pageNumber === 2 && currentPage > 3 ||
                    pageNumber === totalPages - 1 && currentPage < totalPages - 2
                  ) {
                    return (
                      <li key={pageNumber} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  
                  return null;
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity; 